import { IndexTable, Text } from '@shopify/polaris';
import { useEffect, useState } from 'react';

/**
 * Hermana de TransferAddStockTable, pero pensada para anotar recepción de
 * mercancía cuando la transferencia está en IN_TRANSIT.
 *
 * Tres filas:
 *   1. ENVIADO   — qty con método H ya en tránsito (sentQty del stock).
 *   2. RECIBIDO  — qty D ya anotada (receivedQty del stock).
 *   3. AÑADIR    — input por variante: cuántas RECIBES AHORA además de lo
 *                  que ya estaba anotado. Puede ser negativo para corregir
 *                  hacia abajo (p. ej. te equivocaste y dijiste 5 cuando
 *                  eran 4 → anotas -1).
 *
 * Importante (contrato con el back):
 *   El endpoint POST /transfers/:id/receive espera el TOTAL recibido, no el
 *   delta. Internamente hace `nuevoTotal - currentReceivedStock` para calcular
 *   cuánto añadir o restar a la cuenta D. La UX aquí es "añade lo nuevo"
 *   porque es lo intuitivo, así que la tabla se encarga de convertir antes
 *   de emitir:
 *
 *     totalParaElBack = receivedQty + lo_que_anota_el_operador
 *
 *   El padre (ReceiveStockCard) lo manda al back tal cual como
 *   `consolidatedQty`. Esto evita confusiones del tipo "puse 4 y no sumó
 *   porque ya había 2".
 *
 * El shape de `product.stock` viene del endpoint /transfers/:id/stock y
 * trae sentQty/receivedQty (no `qty` plana como en /products).
 */
const TransferReceiveStockTable = ({ product, onAddQty }) => {
  const headings = [{ title: '' }, ...product.variants.map((v) => ({ title: v.title })), { title: 'TOTAL' }];

  // El input arranca en 0: representa "cuántas estoy añadiendo ahora".
  const initialAdditions = (p) =>
    p.variants.map(({ barcode }) => ({ barcode, qty: 0 }));

  const [additions, setAdditions] = useState(initialAdditions(product));

  // Si cambia el producto seleccionado o se refresca el stock tras anotar,
  // reseteamos las adiciones a 0 para no arrastrar valores stale.
  useEffect(() => {
    setAdditions(initialAdditions(product));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  useEffect(() => {
    // El back espera total nuevo. Sumamos al receivedQty y emitimos.
    const totals = product.variants
      .map(({ barcode }) => {
        const s = product.stock?.find((st) => st.barcode === barcode);
        const currentReceived = s?.receivedQty ?? 0;
        const addition = additions.find((a) => a.barcode === barcode)?.qty ?? 0;
        return { barcode, qty: currentReceived + addition, addition };
      })
      .filter(({ addition }) => addition !== 0)
      .map(({ barcode, qty }) => ({ barcode, qty }));

    onAddQty(totals);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, additions]);

  return (
    <IndexTable headings={headings} itemCount={3} selectable={false} lastColumnSticky={true}>
      <SentRow product={product} />
      <ReceivedRow product={product} additions={additions} />
      <InputRow product={product} additions={additions} setAdditions={setAdditions} />
    </IndexTable>
  );
};

const sentByVariant = (product) =>
  product.variants.map((v) => {
    const s = product.stock?.find((st) => st.barcode === v.barcode);
    return { barcode: v.barcode, qty: s?.sentQty ?? 0 };
  });

const receivedByVariant = (product) =>
  product.variants.map((v) => {
    const s = product.stock?.find((st) => st.barcode === v.barcode);
    return { barcode: v.barcode, qty: s?.receivedQty ?? 0 };
  });

const sumQty = (rows) => rows.reduce((acc, r) => acc + r.qty, 0);

const SentRow = ({ product }) => {
  const sent = sentByVariant(product);
  return (
    <IndexTable.Row subdued selected={false}>
      <IndexTable.Cell>
        <Text alignment="start" variant="headingXs" fontWeight="bold">
          ENVIADO
        </Text>
      </IndexTable.Cell>
      {sent.map((s, i) => (
        <IndexTable.Cell key={i}>
          <Text alignment="center" variant="headingXs" fontWeight="regular">
            {s.qty}
          </Text>
        </IndexTable.Cell>
      ))}
      <IndexTable.Cell>
        <Text alignment="center" variant="headingXs" fontWeight="bold">
          {sumQty(sent)}
        </Text>
      </IndexTable.Cell>
    </IndexTable.Row>
  );
};

const ReceivedRow = ({ product, additions }) => {
  const received = receivedByVariant(product);
  // Cuando hay adición pendiente, mostramos "antes -> después" para que el
  // operador vea de un vistazo a cuánto va a quedar el total recibido.
  const additionFor = (barcode) => additions.find((a) => a.barcode === barcode)?.qty ?? 0;

  return (
    <IndexTable.Row subdued selected={false}>
      <IndexTable.Cell>
        <Text alignment="start" variant="headingXs" fontWeight="bold">
          RECIBIDO
        </Text>
      </IndexTable.Cell>
      {received.map((s, i) => {
        const add = additionFor(s.barcode);
        return (
          <IndexTable.Cell key={i}>
            <Text alignment="center" variant="headingXs" fontWeight="regular">
              {s.qty}
              {add !== 0 ? ` → ${s.qty + add}` : ''}
            </Text>
          </IndexTable.Cell>
        );
      })}
      <IndexTable.Cell>
        <Text alignment="center" variant="headingXs" fontWeight="bold">
          {sumQty(received)}
          {sumQty(additions) !== 0 ? ` → ${sumQty(received) + sumQty(additions)}` : ''}
        </Text>
      </IndexTable.Cell>
    </IndexTable.Row>
  );
};

const InputRow = ({ product, additions, setAdditions }) => {
  return (
    <IndexTable.Row subdued selected={false}>
      <IndexTable.Cell>
        <Text alignment="start" variant="headingXs" fontWeight="bold">
          AÑADIR
        </Text>
      </IndexTable.Cell>
      {product.variants
        .map((v) => additions.find((a) => a.barcode === v.barcode))
        .map((a, i) => (
          <IndexTable.Cell key={i}>
            <input
              type="number"
              value={a?.qty ?? 0}
              style={{ width: '50px', borderBottom: '1px solid black', textAlign: 'center' }}
              onChange={(e) =>
                setAdditions([
                  ...additions.filter((x) => x.barcode !== a.barcode),
                  {
                    barcode: a.barcode,
                    qty: e.target.value === '' ? 0 : parseInt(e.target.value, 10),
                  },
                ])
              }
            />
          </IndexTable.Cell>
        ))}
      <IndexTable.Cell>
        <Text alignment="center" variant="headingXs" fontWeight="bold">
          {sumQty(additions)}
        </Text>
      </IndexTable.Cell>
    </IndexTable.Row>
  );
};

export default TransferReceiveStockTable;
